import { useState } from "react";
import CollapseButton from "../atoms/CollapseButton";

/**
 * ShopRegiTree - 店舗→レジ の2階層ツリー構造（共通基盤）
 *
 * グループ化・展開/折りたたみの状態管理のみを担当し、表示内容は呼び出し側に委ねる。
 * AllShopsTreeView（月次データ表）・CommentTreeView（コメント表示）で共用する。
 *
 * @param {object[]}    regiInfo              - 全レジ情報 [{ShopNo, ShopName, RegiNo, RegiName}]
 * @param {Set<number>} defaultExpandedShops  - 初期展開する店舗番号の集合
 * @param {Set<string>} defaultExpandedRegis  - 初期展開するレジキー（`${ShopNo}-${RegiNo}`）の集合
 * @param {function}    [renderShopBadge]     - (shopNo) => ReactNode|null 店舗ヘッダーに表示する付加要素
 * @param {function}    [renderRegiBadge]     - (shopNo, regiNo) => ReactNode|null レジヘッダーに表示する付加要素
 * @param {function}    renderRegiContent     - (shopNo, regiNo) => ReactNode レジ展開時の内容
 */
function ShopRegiTree({
  regiInfo,
  defaultExpandedShops,
  defaultExpandedRegis,
  renderShopBadge,
  renderRegiBadge,
  renderRegiContent,
}) {
  const [expandedShops, setExpandedShops] = useState(() => new Set(defaultExpandedShops));
  const [expandedRegis, setExpandedRegis] = useState(() => new Set(defaultExpandedRegis));

  // ShopNo でグループ化（ShopNo=0 の事務所アカウントは除外）
  const shopMap = {};
  for (const r of regiInfo) {
    if (r.ShopNo === 0) continue;
    if (!shopMap[r.ShopNo]) {
      shopMap[r.ShopNo] = { shopName: r.ShopName, regis: [] };
    }
    shopMap[r.ShopNo].regis.push(r);
  }

  const toggleShop = (shopNo) => {
    setExpandedShops((prev) => {
      const next = new Set(prev);
      next.has(shopNo) ? next.delete(shopNo) : next.add(shopNo);
      return next;
    });
  };

  const toggleRegi = (key) => {
    setExpandedRegis((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  return (
    <div className="space-y-1">
      {Object.entries(shopMap).map(([shopNo, { shopName, regis }]) => {
        const shopExpanded = expandedShops.has(Number(shopNo));
        return (
          <div key={shopNo}>
            {/* 店舗ヘッダー行 */}
            <div
              className="flex items-center gap-2 px-3 py-2 bg-gray-200 rounded cursor-pointer select-none hover:bg-gray-300"
              onClick={() => toggleShop(Number(shopNo))}
            >
              {/* stopPropagation でクリックが親divに二重発火するのを防ぐ */}
              <span onClick={(e) => e.stopPropagation()}>
                <CollapseButton
                  isExpanded={shopExpanded}
                  onToggle={() => toggleShop(Number(shopNo))}
                />
              </span>
              <span className="font-medium text-sm">{shopName}</span>
              {renderShopBadge?.(Number(shopNo))}
            </div>

            {/* レジ一覧（店舗展開時） */}
            {shopExpanded && (
              <div className="ml-6 mt-1 space-y-1">
                {regis.map((regi) => {
                  const regiKey = `${regi.ShopNo}-${regi.RegiNo}`;
                  const regiExpanded = expandedRegis.has(regiKey);
                  return (
                    <div key={regiKey}>
                      {/* レジヘッダー行 */}
                      <div
                        className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded cursor-pointer select-none hover:bg-gray-200"
                        onClick={() => toggleRegi(regiKey)}
                      >
                        <span onClick={(e) => e.stopPropagation()}>
                          <CollapseButton
                            isExpanded={regiExpanded}
                            onToggle={() => toggleRegi(regiKey)}
                          />
                        </span>
                        <span className="text-sm">
                          {regi.RegiNo}番レジ（{regi.RegiName}）
                        </span>
                        {renderRegiBadge?.(regi.ShopNo, regi.RegiNo)}
                      </div>

                      {/* 内容（レジ展開時） */}
                      {regiExpanded && renderRegiContent(regi.ShopNo, regi.RegiNo)}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default ShopRegiTree;
